import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

// Vibrant solid colors - same as RetroWordmark
const colors = [
  '#FF00FF', // Hot pink
  '#FF6B00', // Orange
  '#00FF9F', // Neon green
  '#FF1493', // Deep pink
  '#FFB800', // Amber
  '#00FFE0', // Cyan
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

// Font style variations - using VT323
const fontStyles = [
  'tracking-tighter',
  'tracking-normal',
  'tracking-wide',
  'tracking-wider',
  'tracking-widest'
];

const RetroLoadingDots = ({ size = '1em', className, current = 1, total = 1, text, spiceEmojis = [] }) => {
  const baseText = text || `${current} of ${total}`;
  const totalLength = baseText.length + spiceEmojis.length; // No space needed since emojis are below
  
  const [letterStates, setLetterStates] = useState(() => {
    return Array(totalLength).fill(0).map(() => ({
      color: colors[Math.floor(Math.random() * colors.length)],
      style: fontStyles[Math.floor(Math.random() * fontStyles.length)],
      scale: 0.8 + Math.random() * 0.4,
      rotation: Math.random() * 8 - 4,
      y: Math.random() * 6 - 3
    }));
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setLetterStates(prev => 
        prev.map(() => ({
          color: colors[Math.floor(Math.random() * colors.length)],
          style: fontStyles[Math.floor(Math.random() * fontStyles.length)],
          scale: 0.8 + Math.random() * 0.4,
          rotation: Math.random() * 8 - 4,
          y: Math.random() * 6 - 3
        }))
      );
    }, 800);

    return () => clearInterval(interval);
  }, []);

  // Update states when text/current/total/spices change
  useEffect(() => {
    const newTotalLength = baseText.length + spiceEmojis.length;
    setLetterStates(prev => {
      if (prev.length !== newTotalLength) {
        return Array(newTotalLength).fill(0).map(() => ({
          color: colors[Math.floor(Math.random() * colors.length)],
          style: fontStyles[Math.floor(Math.random() * fontStyles.length)],
          scale: 0.8 + Math.random() * 0.4,
          rotation: Math.random() * 8 - 4,
          y: Math.random() * 6 - 3
        }));
      }
      return prev;
    });
  }, [current, total, text, spiceEmojis, baseText]);

  return (
    <motion.div 
      className={`select-none flex ${spiceEmojis.length > 0 ? 'flex-col' : 'flex-row'} gap-1 items-center justify-center ${className}`}
      style={{ fontSize: size }}
    >
      {/* Render base text characters */}
      <div className="flex gap-1 items-center justify-center">
        {baseText.split('').map((letter, i) => (
          <motion.span
            key={`text-${letter}-${i}`}
            animate={{
              scale: letterStates[i].scale,
              rotate: letterStates[i].rotation,
              y: letterStates[i].y
            }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 15,
              mass: 0.5 + Math.random() * 0.3,
              velocity: 5
            }}
            className={`font-['VT323'] ${letterStates[i].style}`}
            style={{
              color: letterStates[i].color,
              display: 'inline-block',
              transformOrigin: 'center center'
            }}
          >
            {letter}
          </motion.span>
        ))}
      </div>
      
      {/* Render emojis below if they exist */}
      {spiceEmojis.length > 0 && (
        <div className="flex gap-1 items-center justify-center">
          {spiceEmojis.map((emoji, i) => {
            const stateIndex = baseText.length + i;
            return (
              <motion.span
                key={`emoji-${emoji}-${i}`}
                animate={{
                  scale: letterStates[stateIndex].scale * 0.6, // Much smaller scale
                  rotate: letterStates[stateIndex].rotation * 0.3, // Reduced rotation
                  y: letterStates[stateIndex].y * 0.4 // Gentler vertical movement
                }}
                transition={{
                  type: "spring",
                  stiffness: 300, // Reduced stiffness for softer movement
                  damping: 20, // Increased damping for less bouncy
                  mass: 0.8 + Math.random() * 0.2, // Heavier mass for slower movement
                  velocity: 2 // Reduced velocity
                }}
                className={letterStates[stateIndex].style}
                style={{
                  fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Android Emoji", "EmojiSymbols", sans-serif',
                  fontSize: '0.7em', // Smaller font size relative to the text
                  opacity: 0.6, // More subtle opacity
                  display: 'inline-block',
                  transformOrigin: 'center center',
                  filter: 'none' // Ensure emojis aren't affected by any color filters
                }}
              >
                {emoji}
              </motion.span>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default RetroLoadingDots; 